// Live Ticker v1
import { useState, useMemo, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getOgLocale } from "@/lib/seoUtils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GamesPaginatedList } from "@/components/lists/GamesPaginatedList";
import { Hero } from "@/components/sections/Hero";
import { LiveTicker } from "@/components/ticker/LiveTicker";
import { LeagueFilterDropdown } from "@/components/filters/LeagueFilterDropdown";
import { AnalysisDrawer } from "@/components/analysis/AnalysisDrawer";
import { useLeagues } from "@/hooks/useLeagues";
import { Radio, Trophy, List, ArrowLeft, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { AllLeaguesStandingsOverview } from "@/components/tables/AllLeaguesStandingsOverview";
import { StandingsTable } from "@/components/tables/StandingsTable";
import { StandingsTableGrouped } from "@/components/tables/StandingsTableGrouped";
import { useStandingsCache } from "@/hooks/useStandingsCache";
import { useStandingsCacheGrouped } from "@/hooks/useStandingsCacheGrouped";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { supabase } from "@/integrations/supabase/client";
import { getSeasonForLeague } from "@/lib/seasonUtils";
import { useScrollRestore } from "@/hooks/useScrollRestore";

export default function Index() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['dashboard', 'common']);

  // Scroll restoration
  useScrollRestore();

  // ── URL-synced league filter ──────────────────────────────────
  const selectedLeague = useMemo(() => {
    return searchParams.get('league') || 'all';
  }, [searchParams]);

  const setSelectedLeague = useCallback((league: string) => {
    const params = new URLSearchParams(searchParams);
    if (league === 'all') params.delete('league'); else params.set('league', league);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Non-URL state (transient) ─────────────────────────────────
  const [selectedGame, setSelectedGame] = useState<any | null>(null);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [selectedStandingsLeagueId, setSelectedStandingsLeagueId] = useState<number | undefined>(undefined);
  const [defaultTab, setDefaultTab] = useState<string>("analysis");

  const { leagues, loading: leaguesLoading } = useLeagues();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites(user?.id);

  // Convert selected league UUID to API ID using database data
  const selectedLeagueApiId = useMemo(() => {
    if (selectedLeague === "all") return undefined;
    return leagues.find(league => league.id === selectedLeague)?.api_football_id;
  }, [selectedLeague, leagues]);

  // ✅ Query params support (push notifications deep-link)
  useEffect(() => {
    const fixtureId = searchParams.get('fixture');
    const tab = searchParams.get('tab') || 'analysis';
    if (!fixtureId) return;

    (async () => {
      // Buscar jogo direto via fixture_id com JOINs
      const { data: game } = await supabase
        .from('games')
        .select(`
          id,
          fixture_id,
          match_date,
          status,
          status_short,
          home_score,
          away_score,
          elapsed,
          home_team_id,
          away_team_id,
          home_team:teams!games_home_team_id_fkey(id, name, logo_url, api_football_id),
          away_team:teams!games_away_team_id_fkey(id, name, logo_url, api_football_id),
          league:leagues!games_league_id_fkey(name, api_football_id, logo_url)
        `)
        .eq('fixture_id', Number(fixtureId))
        .single();
        
      if (game) {
        setSelectedGame(game);
        setDefaultTab(tab);
        setIsAnalysisOpen(true);
      }
      setSearchParams({});
    })();
  }, [searchParams, setSearchParams]);

  const handleViewAnalysis = (game: any) => {
    setSelectedGame(game);
    setDefaultTab("analysis");
    setIsAnalysisOpen(true);
  };

  const handleCloseAnalysis = () => {
    setIsAnalysisOpen(false);
    setSelectedGame(null);
  };

  const handleShowStandings = () => {
    console.log("Show standings for:", selectedLeague);
  };

  // Helper component to display standings for a specific league
  const StandingsDisplay = ({ leagueApiId }: { leagueApiId: number }) => {
    // Check if league uses grouped standings (World Cup Qualification)
    const isGroupedLeague = leagueApiId === 32;
    
    // WC Qual Europe (ID 32) uses season 2024, otherwise dynamic per league
    const season = leagueApiId === 32 ? 2024 : getSeasonForLeague(leagueApiId);

    const { standings, loading: regularLoading, leagueInfo: regularLeagueInfo } = useStandingsCache({
      leagueApiId: !isGroupedLeague ? leagueApiId : undefined,
      season
    });

    const { groupedStandings, loading: groupedLoading, leagueInfo: groupedLeagueInfo } = useStandingsCacheGrouped({
      leagueApiId: isGroupedLeague ? leagueApiId : undefined,
      season
    });

    const loading = isGroupedLeague ? groupedLoading : regularLoading;
    const leagueInfo = isGroupedLeague ? groupedLeagueInfo : regularLeagueInfo;

    if (loading) {
      return (
        <div className="space-y-4">
          <Button
            onClick={() => setSelectedStandingsLeagueId(undefined)}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:back')}
          </Button>
          <Skeleton className="h-[400px] w-full" />
        </div>
      );
    }

    if (isGroupedLeague) {
      if (!groupedStandings || groupedStandings.length === 0) {
        return (
          <div className="space-y-4">
            <Button
              onClick={() => setSelectedStandingsLeagueId(undefined)}
              variant="outline"
              size="sm"
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('common:back')}
            </Button>
            <div className="text-center py-8">
              <p className="text-gray-medium">{t('dashboard:noStandings')}</p>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <Button
            onClick={() => setSelectedStandingsLeagueId(undefined)}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:back')}
          </Button>
          <StandingsTableGrouped 
            groupedStandings={groupedStandings}
            leagueName={leagueInfo?.name}
            leagueApiId={leagueInfo?.api_football_id}
            leagueLogo={leagueInfo?.logo_url}
            season={season}
          />
        </div>
      );
    }

    if (!standings || standings.length === 0) {
      return (
        <div className="space-y-4">
          <Button
            onClick={() => setSelectedStandingsLeagueId(undefined)}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:back')}
          </Button>
          <div className="text-center py-8">
            <p className="text-gray-medium">{t('dashboard:noStandings')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Button
          onClick={() => setSelectedStandingsLeagueId(undefined)}
          variant="outline"
          size="sm"
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common:back')}
        </Button>
        <StandingsTable 
          standings={standings} 
          leagueName={leagueInfo?.name}
          leagueApiId={leagueInfo?.api_football_id}
          leagueLogo={leagueInfo?.logo_url}
          season={season}
        />
      </div>
    );
  };

  if (leaguesLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="relative">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <div className="absolute inset-0 rounded-full h-8 w-8 border-2 border-primary/30 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('dashboard:metaTitle')}</title>
        <meta name="description" content={t('dashboard:metaDescription')} />
        <meta name="keywords" content={t('dashboard:seoKeywords')} />
        <link rel="canonical" href="https://studiogool7.com/" />
        <meta property="og:title" content={t('dashboard:metaTitle')} />
        <meta property="og:description" content={t('dashboard:metaDescription')} />
        <meta property="og:url" content="https://studiogool7.com/" />
        <meta property="og:image" content="https://storage.googleapis.com/sg7-public-assets/sg7-social-card.png" />
        <meta property="og:locale" content={getOgLocale(i18n.language)} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <h1 className="sr-only">StudioGool7 — Advanced Football Analytics, Live Match Insights & Betting Picks</h1>
      <Hero />

      {/* Live Ticker — replaces LeagueBar */}
      <LiveTicker onSelectGame={handleViewAnalysis} />
      
      <div className="w-full max-w-sm md:max-w-[90%] lg:max-w-6xl xl:max-w-7xl mx-auto px-4 py-4 space-y-4 md:px-6 md:py-6 md:space-y-6">
        {/* Main Content Tabs */}
        <Tabs defaultValue="games" className="mt-2 md:mt-4">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <LeagueFilterDropdown
              selectedLeague={selectedLeague}
              onLeagueChange={setSelectedLeague}
              leagues={leagues}
            />
            <TabsList className="grid flex-1 max-w-md md:max-w-xl lg:max-w-2xl grid-cols-3 md:grid-cols-4">
              <TabsTrigger value="games" className="text-xs md:text-sm font-bold h-10 md:h-8 min-h-[44px] md:min-h-auto touch-manipulation">
                <Trophy size={16} className="mr-1 md:mr-2 flex-shrink-0" />
                <span>{t('dashboard:tabs.fixtures')}</span>
              </TabsTrigger>
              <TabsTrigger value="live" className="text-xs md:text-sm font-bold h-10 md:h-8 min-h-[44px] md:min-h-auto touch-manipulation">
                <Radio size={16} className="mr-1 md:mr-2 flex-shrink-0" />
                <span>{t('dashboard:tabs.live')}</span>
              </TabsTrigger>
              {/* Table - Hidden on mobile, visible on desktop */}
              <TabsTrigger value="standings" className="hidden md:inline-flex font-bold h-10 md:h-8 min-h-[44px] md:min-h-auto touch-manipulation">
                <List size={16} className="mr-2 flex-shrink-0" />
                <span>{t('dashboard:tabs.standings')}</span>
              </TabsTrigger>
              {/* SG7 Picks - Navigation button styled as TabsTrigger */}
              <button
                onClick={() => navigate('/sg7-picks')}
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm ring-offset-background transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "sketch-border hover-sketch",
                  "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
                  "font-bold h-10 md:h-8 min-h-[44px] md:min-h-auto touch-manipulation"
                )}
              >
                <Target size={16} className="mr-0.5 md:mr-2 flex-shrink-0" />
                <span className="hidden lg:inline">SG7 Picks</span>
                <span className="lg:hidden">SG7</span>
              </button>
            </TabsList>
          </div>

          <TabsContent value="games" className="mt-3 md:mt-4">
            <GamesPaginatedList
              onViewAnalysis={handleViewAnalysis}
              defaultLeagueId={selectedLeagueApiId}
              showFilters={false}
              isFavorite={isFavorite}
              onToggleFavorite={toggleFavorite}
            />
          </TabsContent>

          <TabsContent value="live" className="mt-3 md:mt-4">
            <GamesPaginatedList
              onViewAnalysis={handleViewAnalysis}
              defaultLeagueId={selectedLeagueApiId}
              showFilters={false}
              defaultStatus="live"
              isFavorite={isFavorite}
              onToggleFavorite={toggleFavorite}
            />
          </TabsContent>

          <TabsContent value="standings" className="mt-3 md:mt-4">
            {!selectedStandingsLeagueId ? (
              <AllLeaguesStandingsOverview
                onSelectLeague={setSelectedStandingsLeagueId}
              />
            ) : (
              <StandingsDisplay leagueApiId={selectedStandingsLeagueId} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Analysis Drawer */}
      <AnalysisDrawer
        isOpen={isAnalysisOpen}
        onClose={handleCloseAnalysis}
        defaultTab={defaultTab}
        gameData={selectedGame ? {
          id: selectedGame.id,
          homeTeam: { 
            name: selectedGame.home_team?.name || selectedGame.home_team || "TBD", 
            logo: selectedGame.home_team?.logo_url || selectedGame.home_team_logo || "" 
          },
          awayTeam: { 
            name: selectedGame.away_team?.name || selectedGame.away_team || "TBD", 
            logo: selectedGame.away_team?.logo_url || selectedGame.away_team_logo || "" 
          },
          league: selectedGame.league?.name || selectedGame.league || "League",
          leagueLogo: selectedGame.league?.logo_url || selectedGame.league_logo_url || "",
          date: selectedGame.match_date,
          time: selectedGame.match_date,
          status: selectedGame.status,
          homeScore: selectedGame.home_score,
          awayScore: selectedGame.away_score,
          fixtureId: selectedGame.fixture_id,
          homeTeamId: selectedGame.home_team?.id || selectedGame.home_team_id,
          awayTeamId: selectedGame.away_team?.id || selectedGame.away_team_id,
          homeTeamApiId: selectedGame.home_team?.api_football_id || selectedGame.home_team_api_id,
          awayTeamApiId: selectedGame.away_team?.api_football_id || selectedGame.away_team_api_id
        } : null}
      />
    </>
  );
}
